/**
 * Navigationsbereiche nach Kapitel 38.
 *
 * Beide Listen stehen hier zusammen, weil sie dieselben Ziele teilen: Wer auf dem
 * Telefon „Objekte" antippt und am Tablett „Objekte" anklickt, muss auf derselben
 * Seite landen. Getrennte Listen in getrennten Dateien wären die erste Stelle,
 * an der das auseinanderläuft.
 *
 * Zwei Kennzeichnungen, mit unterschiedlicher Bedeutung:
 *  - `to: null` — der Eintrag führt nirgendwohin, sondern öffnet die Übersicht
 *    der weiteren Bereiche (`opensMore`). Das ist auf dem Telefon der Eintrag
 *    „Mehr"; in `DESKTOP_NAV` gibt es keinen solchen Eintrag, dort hat jeder
 *    Bereich seine Ansicht — deshalb verlangt `NavTarget` dort einen Pfad.
 *  - `pending: true` — die Ansicht existiert, die Daten fehlen noch. Derzeit
 *    trägt kein Bereich diese Marke: „Aufgaben" führte sie bis zuletzt mit,
 *    obwohl `/tasks` mit sechs Registerkarten und Serverzählern längst steht.
 *    Eine Wartemarke an einer fertigen Ansicht ist schlimmer als keine — sie
 *    lässt an den gezeigten Zahlen zweifeln.
 */
import type { ComponentType, SVGProps } from "react";

import {
  IconClock,
  IconCamera,
  IconCheck,
  IconBuilding,
  IconCalendar,
  IconContacts,
  IconCosts,
  IconDocuments,
  IconHome,
  IconIssues,
  IconMessages,
  IconMore,
  IconPlan,
  IconPortfolio,
  IconProjects,
  IconReport,
  IconReports,
  IconSettings,
  IconShield,
  IconTasks,
} from "@/components/icons";
import { t } from "@/i18n";

export type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

export interface NavEntry {
  id: string;
  /** Zielpfad, oder `null` beim Eintrag, der die Übersicht öffnet. */
  to: string | null;
  label: string;
  icon: IconComponent;
  /** Ansicht vorhanden, Daten warten auf einen Endpunkt. */
  pending?: boolean;
  /** Öffnet die Übersicht der weiteren Bereiche statt zu navigieren. */
  opensMore?: boolean;
}

/**
 * Eintrag mit gesichertem Ziel.
 *
 * Der Typ hält fest, was in `DESKTOP_NAV` ohnehin gilt: Jeder Bereich hat eine
 * Ansicht. Ohne ihn müsste jede Stelle, die einen Eintrag verlinkt, einen
 * `null`-Fall behandeln, den es nicht gibt — und genau daraus war ein toter
 * Zweig samt Erklärungsfenster „Wartet auf Backend" gewachsen.
 */
export interface NavTarget extends NavEntry {
  to: string;
}

/** Die sieben mobilen Bereiche aus Kapitel 38, in dieser Reihenfolge. */
export const MOBILE_NAV: readonly NavEntry[] = [
  { id: "home", to: "/", label: t.nav.mobile.home, icon: IconHome },
  { id: "properties", to: "/properties", label: t.nav.mobile.properties, icon: IconBuilding },
  { id: "tasks", to: "/tasks", label: t.nav.mobile.tasks, icon: IconTasks },
  { id: "report", to: "/report/new", label: t.nav.mobile.report, icon: IconReport },
  // „Mein Tag" steht **anstelle** des Kalenders, nicht zusätzlich.
  //
  // Bei acht Einträgen bleiben auf 390 px Breite noch 48 px je Ziel — die
  // Beschriftungen standen fast aneinander, und ein Tippziel dieser Größe ist
  // mit Handschuhen nicht sicher zu treffen. Der Kalender bleibt über „Mehr"
  // und die Rechnerbedienung erreichbar; für die Rolle, die dieses Gerät in
  // der Hand hält, ist „Mein Tag" der häufigere Weg.
  { id: "caretaker", to: "/caretaker", label: t.nav.mobile.caretaker, icon: IconTasks },
  { id: "messages", to: "/communication", label: t.nav.mobile.messages, icon: IconMessages },
  { id: "more", to: null, label: t.nav.mobile.more, icon: IconMore, opensMore: true },
];

export interface NavGroup {
  label: string;
  entries: readonly NavTarget[];
}

/** Die vierzehn Desktop-Bereiche aus Kapitel 38, in drei Gruppen gefasst. */
export const DESKTOP_NAV: readonly NavGroup[] = [
  {
    label: t.nav.sectionWork,
    entries: [
      { id: "dashboard", to: "/", label: t.nav.desktop.dashboard, icon: IconHome },
      { id: "caretaker", to: "/caretaker", label: t.nav.desktop.caretaker, icon: IconTasks },
      { id: "tasks", to: "/tasks", label: t.nav.desktop.tasks, icon: IconTasks },
      { id: "issues", to: "/issues", label: t.nav.desktop.issues, icon: IconIssues },
      { id: "inspections", to: "/inspections", label: t.nav.desktop.inspections, icon: IconCheck },
      { id: "calendar", to: "/calendar", label: t.nav.desktop.calendar, icon: IconCalendar },
      { id: "communication", to: "/communication", label: t.nav.desktop.communication, icon: IconMessages },
    ],
  },
  {
    label: t.nav.sectionData,
    entries: [
      { id: "portfolio", to: "/portfolios", label: t.nav.desktop.portfolio, icon: IconPortfolio },
      { id: "properties", to: "/properties", label: t.nav.desktop.properties, icon: IconBuilding },
      { id: "projects", to: "/projects", label: t.nav.desktop.projects, icon: IconProjects },
      { id: "plans", to: "/plans", label: t.nav.desktop.plans, icon: IconPlan },
      { id: "media", to: "/media", label: t.nav.desktop.media, icon: IconCamera },
      { id: "documents", to: "/documents", label: t.nav.desktop.documents, icon: IconDocuments },
    ],
  },
  {
    label: t.nav.sectionBusiness,
    entries: [
      { id: "costs", to: "/costs", label: t.nav.desktop.costs, icon: IconCosts },
      { id: "reports", to: "/reports", label: t.nav.desktop.reports, icon: IconReports },
      { id: "warranties", to: "/warranties", label: t.nav.desktop.warranties, icon: IconClock },
      { id: "contacts", to: "/contacts", label: t.nav.desktop.contacts, icon: IconContacts },
      // Nutzerverwaltung neben dem Konto: Beide beantworten „wer darf hier was",
      // einmal für einen selbst und einmal für die Organisation. Der Eintrag
      // bleibt für alle sichtbar — die Ansicht erklärt beim Öffnen, dass dafür
      // das Verwaltungsrecht fehlt. Ein Menü, das je nach Rolle anders aussieht,
      // macht Rückfragen unbeantwortbar („bei mir steht das nicht").
      { id: "users", to: "/users", label: t.nav.desktop.users, icon: IconShield },
      { id: "account", to: "/account", label: t.nav.desktop.account, icon: IconSettings },
    ],
  },
];
